import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, PlusIcon } from 'lucide-react';
import { useWorkspace } from '../../contexts/AuthContext';
import { emptyQuestion, saveForm } from '../../services/formService';
import { saveLanding } from '../../services/landingService';
import { uid } from '../../utils/format';
import { Form, FormQuestion } from '../../types';
import { PageHeader } from '../../components/crm/PageHeader';
import { QuestionCard } from '../../components/forms/QuestionCard';
import { DevicePreview, Device } from '../../components/shared/DevicePreview';
import { DynamicForm } from '../../components/public/DynamicForm';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Switch } from '../../components/ui/Switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
export function FormEditor() {
  const {
    id = ''
  } = useParams();
  const {
    forms,
    landings
  } = useWorkspace();
  const stored = forms.find(f => f.id === id);
  const [draft, setDraft] = useState<Form | null>(stored ?? null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  useEffect(() => {
    if (stored && (!draft || draft.id !== stored.id)) setDraft(stored);
  }, [stored, draft]);
  const sortedQuestions = useMemo(() => draft ? [...draft.questions].sort((a, b) => a.order - b.order) : [], [draft]);
  if (!draft) {
    return <div className="rounded-lg border border-border bg-background p-10 text-center" data-id="element-810" data-mp="98n2d6tcrzkp">
        <p className="text-sm text-muted-foreground" data-id="element-811" data-mp="4i6ndi9vw5w8">Este formulario no existe.</p>
        <Link to="/app/formularios" className="mt-4 inline-block" data-id="element-812" data-mp="4g3os9uo14fw">
          <Button variant="secondary" data-id="element-813" data-mp="1yrd5i9tmxvdc">Volver a formularios</Button>
        </Link>
      </div>;
  }
  function update(partial: Partial<Form>) {
    setDraft(prev => prev ? {
      ...prev,
      ...partial
    } : prev);
    setSaved(false);
  }
  const privacyNotice = draft.privacyNotice ?? {
    enabled: true,
    text: 'Al enviar este formulario aceptas que utilicemos tus datos para responder a tu solicitud.',
    linkLabel: 'Consulta nuestra política de privacidad.'
  };
  function updateQuestion(question: FormQuestion) {
    update({
      questions: draft!.questions.map(q => q.id === question.id ? question : q)
    });
  }
  function addQuestion() {
    update({
      questions: [...draft!.questions, emptyQuestion(draft!.id, draft!.questions.length + 1)]
    });
  }
  function duplicateQuestion(question: FormQuestion) {
    const copy: FormQuestion = {
      ...JSON.parse(JSON.stringify(question)),
      id: uid('q'),
      label: `${question.label} (copia)`,
      order: draft!.questions.length + 1
    };
    update({
      questions: [...draft!.questions, copy]
    });
  }
  function reorder(targetId: string) {
    if (!dragId || dragId === targetId) return;
    const ids = sortedQuestions.map(q => q.id);
    const fromIndex = ids.indexOf(dragId);
    const toIndex = ids.indexOf(targetId);
    ids.splice(toIndex, 0, ids.splice(fromIndex, 1)[0]);
    update({
      questions: draft!.questions.map(q => ({
        ...q,
        order: ids.indexOf(q.id) + 1
      }))
    });
    setDragId(null);
  }
  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      await saveForm(draft!);
      setSaved(true);
    } catch (err) {
      // Se muestra la causa real y el editor no se cierra ni pierde cambios.
      setSaveError(err instanceof Error ? err.message : 'Error desconocido.');
    } finally {
      setSaving(false);
    }
  }
  async function associateLanding(landingId: string) {
    const landing = landings.find(l => l.id === landingId);
    if (!landing) return;
    await saveLanding({
      ...landing,
      formId: draft!.id
    });
  }
  const linkedLanding = landings.find(l => l.formId === draft.id);
  return <>
      <Link to="/app/formularios" className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-id="element-814" data-mp="2kadguhsslxs4">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-815" data-mp="2o8t9p2bk20dg" />
        Volver a formularios
      </Link>

      <PageHeader title={draft.name} description={`${draft.questions.length} preguntas · ${draft.status === 'activo' ? 'Activo' : draft.status === 'borrador' ? 'Borrador' : 'Inactivo'}`} actions={<>
            {saving ? <span className="self-center text-sm text-muted-foreground" data-id="element-817" data-mp="25c1k6b5f6p0y">
                Guardando…
              </span> : saved ? <span className="flex items-center gap-1.5 self-center text-sm text-green-600" data-id="element-818" data-mp="18uounsnd52sd">
                <CheckIcon className="h-4 w-4" data-id="element-819" data-mp="1cv8ooncn3qaa" /> Guardado correctamente
              </span> : <span className="self-center text-sm text-muted-foreground" data-id="element-820" data-mp="15c962jx062rz">
                Cambios sin guardar
              </span>}
            <Button variant="secondary" onClick={() => window.open(`/formulario/${draft.slug}`, '_blank')} data-id="element-821" data-mp="10cwmq95cbdu5">
              Vista previa pública
            </Button>
            <Button onClick={handleSave} loading={saving} data-id="element-822" data-mp="wddxx2aeeim1">
              Guardar cambios
            </Button>
          </>} data-id="element-816" data-mp="1ritr0f1oxlh5" />

      {saveError && <div role="alert" className="mb-5 flex flex-col gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 sm:flex-row sm:items-center sm:justify-between" data-id="element-823" data-mp="mdn30zhxe1lf">
          <span data-id="element-824" data-mp="cn2b1uwrbvbh">
            <strong className="font-semibold" data-id="element-825" data-mp="colqxkei44cq">Error al guardar. </strong>
            {saveError}
          </span>
          <Button variant="secondary" onClick={handleSave} loading={saving} data-id="element-826" data-mp="8n9uow14gkvk">
            Reintentar
          </Button>
        </div>}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,520px)]" data-id="element-827" data-mp="3oq9wyy9lf4s">
        <div className="min-w-0" data-id="element-828" data-mp="3utyqb4wjp9on">
          <Tabs defaultValue="preguntas" data-id="element-829" data-mp="3l5ku0uy328ri">
            <TabsList data-id="element-830" data-mp="3l4rtibppwfht">
              <TabsTrigger value="preguntas" data-id="element-831" data-mp="3l49nwoh60ox8">Preguntas</TabsTrigger>
              <TabsTrigger value="ajustes" data-id="element-832" data-mp="3q5p70azs8lcz">Ajustes</TabsTrigger>
            </TabsList>

            <TabsContent value="preguntas" data-id="element-833" data-mp="3b86ohqdp74dz">
              <ul className="space-y-3" data-id="element-834" data-mp="3b9hqbknl222g">
                {sortedQuestions.map((question, index) => <QuestionCard key={question.id} question={question} index={index} previousQuestions={sortedQuestions.slice(0, index)} dragging={dragId === question.id} onChange={updateQuestion} onDuplicate={() => duplicateQuestion(question)} onDelete={() => update({
                questions: draft.questions.filter(q => q.id !== question.id)
              })} onDragStart={() => setDragId(question.id)} onDragOver={e => e.preventDefault()} onDrop={() => reorder(question.id)} data-id="element-835" data-mp="3g7001d9fclbi" data-mpi={index} />)}
              </ul>
              {sortedQuestions.length === 0 && <p className="rounded-lg border border-dashed border-border bg-surface p-8 text-center text-sm text-muted-foreground" data-id="element-836" data-mp="qnppjeulohms">
                  Este formulario todavía no tiene preguntas.
                </p>}
              <Button className="mt-4" variant="secondary" leftIcon={<PlusIcon className="h-4 w-4" data-id="element-838" data-mp="2b7b86v2ict9s" />} onClick={addQuestion} data-id="element-837" data-mp="2b7upc9kc3dmr">
                Añadir pregunta
              </Button>
            </TabsContent>

            <TabsContent value="ajustes" data-id="element-839" data-mp="2kwuts8hlv18c">
              <div className="space-y-5 rounded-lg border border-border bg-background p-5" data-id="element-840" data-mp="2kxwdf1cosw1o">
                <Input id="form-name" label="Nombre del formulario" value={draft.name} onChange={e => update({
                name: e.target.value
              })} data-id="element-841" data-mp="1x8bvmls9s4tz" />
                <Textarea id="form-desc" label="Descripción" rows={3} value={draft.description ?? ''} onChange={e => update({
                description: e.target.value
              })} data-id="element-842" data-mp="268qk1u0j601p" />
                <Input id="form-slug" label="Enlace público" helperText={`${window.location.origin}/formulario/${draft.slug}`} value={draft.slug} onChange={e => update({
                slug: e.target.value
              })} data-id="element-843" data-mp="3hn42ukgffpav" />
                <Select id="form-status" label="Estado" options={[{
                value: 'borrador',
                label: 'Borrador'
              }, {
                value: 'activo',
                label: 'Activo'
              }, {
                value: 'inactivo',
                label: 'Inactivo'
              }]} value={draft.status} onChange={e => update({
                status: e.target.value as Form['status']
              })} data-id="element-844" data-mp="1b0pnxox7eb79" />
                <Input id="form-cta" label="Texto del botón de envío" value={draft.submitLabel} onChange={e => update({
                submitLabel: e.target.value
              })} data-id="element-845" data-mp="1yoz4ocfk88rj" />
                <Textarea id="form-success" label="Mensaje de éxito" rows={2} value={draft.successMessage} onChange={e => update({
                successMessage: e.target.value
              })} data-id="element-846" data-mp="23lz9yl4l57hl" />
                <Select id="form-gracias" label="Página de agradecimiento" placeholder="Selecciona una landing" options={landings.map(l => ({
                value: l.slug,
                label: l.name
              }))} value={draft.thankYouSlug ?? ''} onChange={e => update({
                thankYouSlug: e.target.value
              })} data-id="element-847" data-mp="9c42u1kpdv6s" />
                <Input id="form-redirect" label="URL de redirección (opcional)" placeholder="https://…" value={draft.redirectUrl ?? ''} onChange={e => update({
                redirectUrl: e.target.value
              })} data-id="element-848" data-mp="2iqk4r03brb9i" />
                {/* Aviso informativo: no obliga a marcar nada para enviar. */}
                <div className="space-y-4 rounded-xl border border-border bg-surface p-4" data-id="element-849" data-mp="36gelhsqrljz1">
                  <Switch id="form-privacy-enabled" label="Mostrar aviso de privacidad" checked={privacyNotice.enabled} onChange={checked => update({
                  privacyNotice: {
                    ...privacyNotice,
                    enabled: checked
                  }
                })} data-id="element-850" data-mp="31h1viz4ljws8" />
                  <p className="text-xs text-muted-foreground" data-id="element-851" data-mp="1l32123oiszcd">
                    Texto informativo al pie del formulario. No obliga a marcar
                    nada para poder enviarlo.
                  </p>
                  {privacyNotice.enabled && <>
                      <Textarea id="form-privacy-text" label="Texto del aviso" rows={2} value={privacyNotice.text} onChange={e => update({
                    privacyNotice: {
                      ...privacyNotice,
                      text: e.target.value
                    }
                  })} data-id="element-852" data-mp="3i5elhqnqb6l1" />
                      <Input id="form-privacy-link-label" label="Texto del enlace (opcional)" placeholder="Consulta nuestra política de privacidad." value={privacyNotice.linkLabel ?? ''} onChange={e => update({
                    privacyNotice: {
                      ...privacyNotice,
                      linkLabel: e.target.value
                    }
                  })} data-id="element-853" data-mp="4scvdzmbezec" />
                      <Input id="form-privacy-link-url" label="Enlace a tu política de privacidad (opcional)" placeholder="https://tuempresa.com/privacidad" value={privacyNotice.linkUrl ?? ''} onChange={e => update({
                    privacyNotice: {
                      ...privacyNotice,
                      linkUrl: e.target.value
                    }
                  })} data-id="element-854" data-mp="2lj2vk0bhja2e" />
                    </>}
                </div>
                <Select id="form-landing" label="Asociar a una landing" placeholder={linkedLanding ? linkedLanding.name : 'Selecciona una landing'} options={landings.map(l => ({
                value: l.id,
                label: l.name
              }))} value={linkedLanding?.id ?? ''} onChange={e => associateLanding(e.target.value)} data-id="element-855" data-mp="r8uz8vff8hzc" />
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <FormPreviewPanel form={draft} data-id="element-856" data-mp="89qtpwn396cf" />
      </div>
    </>;
}
function FormPreviewPanel({
  form
}: {
  form: Form;
}) {
  const [device, setDevice] = useState<Device>('desktop');
  return <div className="h-[720px] xl:sticky xl:top-24" data-id="element-857" data-mp="22o1ik53oo0i7">
      <DevicePreview device={device} onDeviceChange={setDevice} data-id="element-858" data-mp="22nrrsfatdpbg">
        <div className="bg-surface p-4" data-id="element-859" data-mp="217e94dchduk4">
          <DynamicForm key={form.questions.length} form={form} preview data-id="element-860" data-mp="218fnorspgz6l" />
        </div>
      </DevicePreview>
    </div>;
}